/**
 * The compact 1NL revenue card (E2.1 design spec 11.4/7.6): commission +
 * device-fee sublines under the headline revenue figure, plus a delta chip
 * with comparison context. Rendered ONLY by the caller when the session has
 * revenue:read (DashboardPage gates the whole card, not just its data) --
 * this component has no permission logic of its own, matching the rest of
 * the dashboard's "the fetcher never calls the endpoint without the
 * permission" pattern one level up.
 */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { MoneyText } from '../../components/MoneyText.tsx';
import { useDisplayCurrency } from '../../currency/CurrencyContext.tsx';
import { palette } from '../../theme.ts';
import { bpRateText } from './dashboardHelpers.ts';
import { DeltaChip } from './DeltaChip.tsx';
import { HeadlineCardSkeleton } from './dashboardSkeletons.tsx';
import type { ComparisonContext } from './periodState.ts';

const MTD_FEE_NOTE = 'Device fees are the same flat amount in both periods; only commission is compared by day.';

/** Subline with the rate behind it on an info tooltip rather than a standing
 *  footer line (owner ruling 2026-08-12). The IconButton keeps it reachable by
 *  keyboard -- MUI Tooltip opens on focus as well as hover. */
function SublineRow({
  label,
  minor,
  currencyCode,
  hint,
}: Readonly<{ label: string; minor: number; currencyCode: string | null; hint: string }>): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Typography variant="caption" sx={{ color: palette.textSecondary }}>
          {label}
        </Typography>
        <Tooltip title={hint}>
          <IconButton size="small" aria-label={`About ${label.toLowerCase()}`} sx={{ p: 0.25, color: palette.textSecondary }}>
            <InfoOutlinedIcon sx={{ fontSize: '0.95rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
      <MoneyText minor={minor} currencyCode={currencyCode} size="0.8rem" weight={600} />
    </Box>
  );
}

export interface RevenueCardProps {
  revenueMinor: number;
  previousRevenueMinor: number | null;
  /** The chip's current side when it differs from the headline (the
   *  day-capped MTD basis); defaults to
   *  `revenueMinor`. */
  chipRevenueMinor?: number;
  commissionMinor: number;
  licenceFeeMinor: number;
  commissionRateBp: number;
  deviceMonthlyFeeMinor: number;
  currencyCode: string | null;
  comparisonContext: ComparisonContext;
  /** FIRST load only (owner ruling 2026-07-15): representative skeleton
   *  instead of the card. Like the card itself, the skeleton only ever
   *  renders for a session with revenue:read -- the caller gates the whole
   *  component, loading state included (zero trace). */
  loading?: boolean;
  /** False when the selected period has NO reports (owner ruling 2026-08-12):
   *  the zeros are structural, so no "-100%" chip. */
  showDeltas?: boolean;
}

export function RevenueCard({
  revenueMinor,
  previousRevenueMinor,
  chipRevenueMinor,
  commissionMinor,
  licenceFeeMinor,
  commissionRateBp,
  deviceMonthlyFeeMinor,
  currencyCode,
  comparisonContext,
  loading = false,
  showDeltas = true,
}: Readonly<RevenueCardProps>): React.JSX.Element {
  const { format } = useDisplayCurrency();
  if (loading) return <HeadlineCardSkeleton testId="revenue-skeleton" rows={2} padding={2} />;
  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: palette.textSecondary, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
      >
        1NL revenue
      </Typography>
      <MoneyText minor={revenueMinor} currencyCode={currencyCode} size="1.4rem" weight={800} sx={{ mt: 0.25 }} />
      {showDeltas && (
        <DeltaChip
          metric="revenue"
          current={chipRevenueMinor ?? revenueMinor}
          previous={previousRevenueMinor}
          contextLabel={comparisonContext.label}
          tooltipText={comparisonContext.tooltipText}
          tooltipNote={comparisonContext.toDay === null ? undefined : MTD_FEE_NOTE}
        />
      )}
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <SublineRow
          label="Commission"
          minor={commissionMinor}
          currencyCode={currencyCode}
          hint={`${bpRateText(commissionRateBp)} of gross profit (default rate; per-shop overrides apply)`}
        />
        <SublineRow
          label="Device fees"
          minor={licenceFeeMinor}
          currencyCode={currencyCode}
          hint={`${format(deviceMonthlyFeeMinor, currencyCode)} per device per month (default; per-shop overrides apply)`}
        />
      </Box>
    </Paper>
  );
}

export default RevenueCard;
